import React, { useState, useEffect } from 'react';
import { 
  Typography, 
  Box, 
  Paper, 
  Grid,
  Button,
  Divider,
  List,
  ListItem,
  ListItemText,
  Chip,
  CircularProgress,
  Snackbar,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useNavigate, useParams } from 'react-router-dom';
import Alert from '@material-ui/lab/Alert';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import DirectionsCarIcon from '@material-ui/icons/DirectionsCar';
import BuildIcon from '@material-ui/icons/Build';
import { firestore } from '../../firebase/config';
import { doc, getDoc, deleteDoc } from 'firebase/firestore';

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
  },
  paper: {
    width: '100%',
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
  },
  title: {
    marginBottom: theme.spacing(2),
  },
  divider: {
    margin: theme.spacing(2, 0),
  },
  section: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  sectionTitle: {
    marginBottom: theme.spacing(1),
  },
  chip: {
    margin: theme.spacing(0.5),
  },
  buttons: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: theme.spacing(3),
  },
  button: {
    marginLeft: theme.spacing(1),
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(3),
  },
  listItem: {
    padding: theme.spacing(1, 0),
  },
  listItemLabel: {
    fontWeight: 'bold',
    marginRight: theme.spacing(2),
  },
  vehicleIcon: {
    fontSize: 60,
    marginBottom: theme.spacing(2),
  },
  maintenanceIcon: {
    fontSize: 24,
    marginRight: theme.spacing(1),
    verticalAlign: 'middle',
  },
  featuresList: {
    display: 'flex',
    flexWrap: 'wrap',
    '& > *': {
      margin: theme.spacing(0.5),
    },
  },
}));

const VehicleDetailPage = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const { id } = useParams();
  const [vehicle, setVehicle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  useEffect(() => {
    fetchVehicle();
  }, [id]);

  const fetchVehicle = async () => {
    try {
      // Simulation de récupération depuis Firestore
      // Dans une implémentation réelle, cela serait remplacé par un appel à l'API
      const vehicleDoc = doc(firestore, 'vehicles', id);
      const vehicleSnapshot = await getDoc(vehicleDoc);
      
      if (vehicleSnapshot.exists()) {
        setVehicle({
          id: vehicleSnapshot.id,
          ...vehicleSnapshot.data()
        });
      } else {
        // Si le véhicule n'existe pas, utiliser des données de démonstration
        setVehicle({
          id: id,
          name: 'Bus Premium',
          registrationNumber: 'AB-123-CD',
          type: 'Bus',
          capacity: 50,
          status: 'Active',
          companyId: '1',
          companyName: 'Voyage Express',
          manufactureYear: 2020,
          model: 'Mercedes Tourismo',
          color: 'Blanc',
          fuelType: 'Diesel',
          features: 'WiFi, Climatisation, Prises USB, Toilettes, Écrans individuels',
          maintenanceDate: '2023-03-15',
          createdAt: new Date().toISOString(),
          lastTripDate: '2023-04-01',
          totalTrips: 128,
          totalDistance: 45600,
          nextMaintenanceDate: '2023-06-15'
        });
      }
    } catch (error) {
      console.error('Error fetching vehicle:', error);
      setSnackbar({
        open: true,
        message: 'Erreur lors de la récupération des données du véhicule',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    navigate(`/admin/vehicles/${id}/edit`);
  };

  const handleDelete = () => {
    setOpenDeleteDialog(true);
  };

  const handleConfirmDelete = async () => {
    try {
      // Simulation de suppression dans Firestore
      // Dans une implémentation réelle, cela serait remplacé par un appel à l'API
      await deleteDoc(doc(firestore, 'vehicles', id));
      
      setSnackbar({
        open: true,
        message: 'Véhicule supprimé avec succès',
        severity: 'success'
      });
      
      // Rediriger vers la liste après un court délai
      setTimeout(() => {
        navigate('/admin/vehicles');
      }, 1500);
      
    } catch (error) {
      console.error('Error deleting vehicle:', error);
      setSnackbar({
        open: true,
        message: 'Erreur lors de la suppression du véhicule',
        severity: 'error'
      });
    } finally {
      setOpenDeleteDialog(false);
    }
  };

  const handleCancelDelete = () => {
    setOpenDeleteDialog(false);
  };

  const handleBack = () => {
    navigate('/admin/vehicles');
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Active':
        return 'primary';
      case 'Inactive':
        return 'default';
      case 'Maintenance':
        return 'secondary';
      default:
        return 'default';
    }
  };

  const getVehicleIcon = (type) => {
    switch (type) {
      case 'Bus':
        return <DirectionsCarIcon className={classes.vehicleIcon} style={{ color: '#3f51b5' }} />;
      case 'Minibus':
        return <DirectionsCarIcon className={classes.vehicleIcon} style={{ color: '#2196f3' }} />;
      case 'Van':
        return <DirectionsCarIcon className={classes.vehicleIcon} style={{ color: '#009688' }} />;
      case 'Car':
        return <DirectionsCarIcon className={classes.vehicleIcon} style={{ color: '#4caf50' }} />;
      default:
        return <DirectionsCarIcon className={classes.vehicleIcon} style={{ color: '#757575' }} />;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Non renseigné';
    
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch (error) {
      return 'Date invalide';
    }
  };

  const renderFeatures = (featuresString) => {
    if (!featuresString) return 'Aucune caractéristique renseignée';
    
    const featuresList = featuresString.split(',').map(feature => feature.trim());
    
    return (
      <div className={classes.featuresList}>
        {featuresList.map((feature, index) => (
          <Chip key={index} label={feature} variant="outlined" size="small" />
        ))}
      </div>
    );
  };

  return (
    <Box className={classes.root}>
      <Button
        variant="outlined"
        color="primary"
        startIcon={<ArrowBackIcon />}
        onClick={handleBack}
        style={{ marginBottom: 16 }}
      >
        Retour à la liste
      </Button>
      
      <Typography variant="h4" component="h1" className={classes.title}>
        Détails du véhicule
      </Typography>
      
      {loading ? (
        <div className={classes.loading}>
          <CircularProgress />
        </div>
      ) : vehicle ? (
        <>
          <Paper className={classes.paper}>
            <Grid container spacing={2}>
              <Grid item xs={12} style={{ textAlign: 'center' }}>
                {getVehicleIcon(vehicle.type)}
                <Typography variant="h5" component="h2">
                  {vehicle.name}
                </Typography>
                <Chip 
                  label={vehicle.status} 
                  color={getStatusColor(vehicle.status)} 
                  className={classes.chip}
                />
                <Chip 
                  label={vehicle.type} 
                  variant="outlined" 
                  className={classes.chip}
                />
                {vehicle.capacity && (
                  <Chip 
                    label={`${vehicle.capacity} places`} 
                    variant="outlined" 
                    className={classes.chip}
                  />
                )}
              </Grid>
              
              <Grid item xs={12}>
                <Divider className={classes.divider} />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Typography variant="h6" className={classes.sectionTitle}>
                  Informations générales
                </Typography>
                <List>
                  <ListItem className={classes.listItem}>
                    <ListItemText 
                      primary={
                        <Box display="flex">
                          <Box className={classes.listItemLabel}>Immatriculation:</Box>
                          <Box>{vehicle.registrationNumber}</Box>
                        </Box>
                      } 
                    />
                  </ListItem>
                  <ListItem className={classes.listItem}>
                    <ListItemText 
                      primary={
                        <Box display="flex">
                          <Box className={classes.listItemLabel}>Entreprise:</Box>
                          <Box>{vehicle.companyName}</Box>
                        </Box>
                      } 
                    />
                  </ListItem>
                  <ListItem className={classes.listItem}>
                    <ListItemText 
                      primary={
                        <Box display="flex">
                          <Box className={classes.listItemLabel}>Modèle:</Box>
                          <Box>{vehicle.model || 'Non renseigné'}</Box>
                        </Box>
                      } 
                    />
                  </ListItem>
                  <ListItem className={classes.listItem}>
                    <ListItemText 
                      primary={
                        <Box display="flex">
                          <Box className={classes.listItemLabel}>Année de fabrication:</Box>
                          <Box>{vehicle.manufactureYear || 'Non renseignée'}</Box>
                        </Box>
                      } 
                    />
                  </ListItem>
                  <ListItem className={classes.listItem}>
                    <ListItemText 
                      primary={
                        <Box display="flex">
                          <Box className={classes.listItemLabel}>Couleur:</Box>
                          <Box>{vehicle.color || 'Non renseignée'}</Box>
                        </Box>
                      } 
                    />
                  </ListItem>
                  <ListItem className={classes.listItem}>
                    <ListItemText 
                      primary={
                        <Box display="flex">
                          <Box className={classes.listItemLabel}>Type de carburant:</Box>
                          <Box>{vehicle.fuelType || 'Non renseigné'}</Box>
                        </Box>
                      } 
                    />
                  </ListItem>
                </List>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Typography variant="h6" className={classes.sectionTitle}>
                  Maintenance
                </Typography>
                <List>
                  <ListItem className={classes.listItem}>
                    <ListItemText 
                      primary={
                        <Box display="flex">
                          <Box className={classes.listItemLabel}>Dernière maintenance:</Box>
                          <Box>{formatDate(vehicle.maintenanceDate)}</Box>
                        </Box>
                      } 
                    />
                  </ListItem>
                  {vehicle.nextMaintenanceDate && (
                    <ListItem className={classes.listItem}>
                      <ListItemText 
                        primary={
                          <Box display="flex">
                            <Box className={classes.listItemLabel}>Prochaine maintenance:</Box>
                            <Box>
                              <BuildIcon className={classes.maintenanceIcon} color="secondary" />
                              {formatDate(vehicle.nextMaintenanceDate)}
                            </Box>
                          </Box>
                        } 
                      />
                    </ListItem>
                  )}
                </List>
                
                <Typography variant="h6" className={classes.sectionTitle} style={{ marginTop: 24 }}>
                  Statistiques
                </Typography>
                <List>
                  {vehicle.totalTrips !== undefined && (
                    <ListItem className={classes.listItem}>
                      <ListItemText 
                        primary={
                          <Box display="flex">
                            <Box className={classes.listItemLabel}>Nombre de voyages:</Box>
                            <Box>{vehicle.totalTrips}</Box>
                          </Box>
                        } 
                      />
                    </ListItem>
                  )}
                  {vehicle.totalDistance !== undefined && (
                    <ListItem className={classes.listItem}>
                      <ListItemText 
                        primary={
                          <Box display="flex">
                            <Box className={classes.listItemLabel}>Distance totale:</Box>
                            <Box>{vehicle.totalDistance} km</Box>
                          </Box>
                        } 
                      />
                    </ListItem>
                  )}
                  {vehicle.lastTripDate && (
                    <ListItem className={classes.listItem}>
                      <ListItemText 
                        primary={
                          <Box display="flex">
                            <Box className={classes.listItemLabel}>Dernier voyage:</Box>
                            <Box>{formatDate(vehicle.lastTripDate)}</Box>
                          </Box>
                        } 
                      />
                    </ListItem>
                  )}
                  <ListItem className={classes.listItem}>
                    <ListItemText 
                      primary={
                        <Box display="flex">
                          <Box className={classes.listItemLabel}>Créé le:</Box>
                          <Box>{formatDate(vehicle.createdAt)}</Box>
                        </Box>
                      } 
                    />
                  </ListItem>
                </List>
              </Grid>
              
              <Grid item xs={12}>
                <Divider className={classes.divider} />
              </Grid>
              
              <Grid item xs={12}>
                <Typography variant="h6" className={classes.sectionTitle}>
                  Caractéristiques
                </Typography>
                {renderFeatures(vehicle.features)}
              </Grid>
            </Grid>
            
            <div className={classes.buttons}>
              <Button
                variant="contained"
                color="secondary"
                startIcon={<DeleteIcon />}
                onClick={handleDelete}
              >
                Supprimer
              </Button>
              <Button
                variant="contained"
                color="primary"
                startIcon={<EditIcon />}
                onClick={handleEdit}
              >
                Modifier
              </Button>
            </div>
          </Paper>
        </>
      ) : (
        <Paper className={classes.paper}>
          <Typography variant="body1">
            Véhicule non trouvé
          </Typography>
        </Paper>
      )}
      
      {/* Dialog de confirmation de suppression */}
      <Dialog
        open={openDeleteDialog}
        onClose={handleCancelDelete}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{"Confirmer la suppression"}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Êtes-vous sûr de vouloir supprimer le véhicule {vehicle?.name} ({vehicle?.registrationNumber}) ? Cette action est irréversible.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete} color="primary">
            Annuler
          </Button>
          <Button onClick={handleConfirmDelete} color="secondary" autoFocus>
            Supprimer
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Snackbar pour les notifications */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default VehicleDetailPage;
